import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@siliconx/shared/ui/utils';
import { getValidIdToken, clearClientIdentity, fetchClientIdentity } from '../lib/auth';
import { api, API_BASE, throwIfNotOk } from '../lib/api';
import {
  ASSET_IMPORT_ASSET_CLASSES,
  ASSET_IMPORT_COLUMNS,
  assetImportTemplateCsv,
} from '@siliconx/shared/import-template';
import {
  ImportRowIssues,
  type ImportRowErrors,
  type ImportRowWarnings,
} from '../components/onboarding/ImportRowIssues';
import { qk } from '../lib/queryKeys';
import {
  addStoredBatch,
  clearStoredBatches,
  loadStoredBatches,
  removeStoredBatch,
} from '../lib/onboardingBatches';
import type { ReviewPayload } from '../lib/importReview';
import { Button } from '@siliconx/shared/ui/Button';
import { Card } from '@siliconx/shared/ui/Card';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { requireOrgFeature } from '../lib/orgFeatureGate';

// SIL-1108: `orgFeature: 'onboarding'` (customerNav.ts) is business-only —
// gated here so a direct hit doesn't fire this page's mount-time fetch
// before the root `OrgFeatureGate`'s (__root.tsx) redirect lands.
export const Route = createFileRoute('/onboarding')({
  beforeLoad: () => requireOrgFeature('onboarding'),
  component: OnboardingPage,
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BatchMeta {
  batchId: string;
  rowCountAccepted: number;
  rowCountTotal: number;
  /** Template example rows dropped on import (SIL-15); response-only. */
  rowCountSkipped?: number;
  rowErrors: ImportRowErrors[];
  rowWarnings?: ImportRowWarnings[];
  /** label shown in UI; defaults to batchId */
  label?: string;
}

interface PrometheusTarget {
  targetId: string;
  label: string;
}

interface BatchAssignment {
  batchId: string;
  method: 'csv' | 'prometheus';
  prometheusTargetId?: string;
}

interface WizardState {
  step: 1 | 2 | 3;
  // Step 1 outputs (Upload CSV)
  batches: BatchMeta[];
  // Step 2 outputs (Telemetry)
  targets: PrometheusTarget[];
  batchAssignments: BatchAssignment[];
}

// ---------------------------------------------------------------------------
// Shared UI components
// ---------------------------------------------------------------------------

function inputCls(hasError = false) {
  return cn(
    'w-full rounded-md border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-500',
    hasError ? 'border-red-400 focus:ring-red-400' : 'border-border focus:border-brand-500',
  );
}

function Field({
  label,
  error,
  required,
  hint,
  children,
}: {
  label: string;
  error?: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-foreground">
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      {children}
      {hint && !error && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

// Country codes + display names derived from the browser's own ISO 3166
// data (mirrors locations.tsx's COUNTRIES helper) — no hand-maintained list,
// but here we need the alpha-2 CODE (customer_org_jurisdiction.country_code,
// SIL-642), not just a display name, so this keeps the pair.
const COUNTRY_OPTIONS: { code: string; name: string }[] = (() => {
  if (typeof Intl.DisplayNames !== 'function') return [];
  const names = new Intl.DisplayNames(['en'], { type: 'region', fallback: 'code' });
  const out: { code: string; name: string }[] = [];
  for (let a = 65; a <= 90; a++) {
    for (let b = 65; b <= 90; b++) {
      const code = String.fromCharCode(a, b);
      const name = names.of(code);
      if (name && name !== code) out.push({ code, name });
    }
  }
  return out.sort((x, y) => x.name.localeCompare(y.name));
})();

// ---------------------------------------------------------------------------
// Step indicator
// ---------------------------------------------------------------------------

const STEPS = [
  { n: 1, label: 'Upload CSV' },
  { n: 2, label: 'Telemetry' },
  { n: 3, label: 'Done' },
] as const;

function StepIndicator({ current }: { current: number }) {
  const steps = STEPS;
  return (
    <ol className="flex items-center gap-0">
      {steps.map(({ n, label }, i) => {
        const done = n < current;
        const active = n === current;
        return (
          <li key={n} className="flex items-center">
            <div className="flex flex-col items-center">
              <div
                className={cn(
                  'flex size-8 items-center justify-center rounded-full text-sm font-semibold',
                  done && 'bg-brand-600 text-white',
                  active && 'bg-brand-600 text-white ring-4 ring-brand-100',
                  !done && !active && 'bg-muted text-muted-foreground',
                )}
              >
                {done ? '✓' : n}
              </div>
              <span
                className={cn(
                  'mt-1 text-xs',
                  active ? 'font-semibold text-brand-700' : 'text-muted-foreground',
                )}
              >
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                className={cn(
                  'mx-2 mb-5 h-px w-12',
                  done ? 'bg-brand-400' : 'bg-border',
                )}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — Upload CSV
// ---------------------------------------------------------------------------

/** Failed upload (HTTP 422, 0 rows accepted): shown inline, never added as a batch. */
interface RejectedUpload {
  filename: string;
  message: string;
  rowErrors: ImportRowErrors[];
  rowWarnings: ImportRowWarnings[];
}

/** Trigger a client-side download of the canonical CSV template (SIL-15). */
function downloadAssetTemplate() {
  const blob = new Blob([assetImportTemplateCsv()], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'siliconx-asset-import-template.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function Step1({
  state,
  onNext,
  onBack,
}: {
  state: WizardState;
  onNext: (updates: Pick<WizardState, 'batches'>) => void;
  onBack: () => void;
}) {
  const navigate = useNavigate();
  const [batches, setBatches] = useState<BatchMeta[]>(state.batches);
  const [assetClass, setAssetClass] = useState<string>(ASSET_IMPORT_ASSET_CLASSES[0].id);
  const [rejected, setRejected] = useState<RejectedUpload | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function handleUpload(ev: React.FormEvent) {
    ev.preventDefault();
    if (!file) { setUploadErr('Please select a CSV file.'); return; }
    setUploadErr(null);
    setRejected(null);
    setUploading(true);
    try {
      const token = await getValidIdToken();
      const formData = new FormData();
      formData.append('file', file);
      formData.append('assetClassId', assetClass);
      // SIL-1617: opt in to the mapping step. Without this the API keeps its
      // original contract (rows staged immediately, columns matched by name).
      formData.append('mapping', 'review');

      // Absolute URL — no same-origin /api proxy in prod. No Content-Type header:
      // the browser sets the multipart/form-data boundary for FormData bodies.
      const res = await fetch(`${API_BASE}/api/client/import/batches`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as {
          error?: string;
          code?: string;
          rowErrors?: ImportRowErrors[];
          rowWarnings?: ImportRowWarnings[];
        };
        // SIL-1589: 0 accepted rows is a failed upload. Show every rejected
        // row; do not add it to the batch list (it is not onboarding progress).
        if (body.code === 'all_rows_rejected') {
          setRejected({
            filename: file.name,
            message: body.error ?? 'Every row was rejected. Nothing was imported.',
            rowErrors: body.rowErrors ?? [],
            rowWarnings: body.rowWarnings ?? [],
          });
          return;
        }
        throw new Error(body.error ?? `Upload failed (HTTP ${res.status})`);
      }
      // SIL-1617: every accepted upload continues on the batch review page
      // (mapping step for seller-shaped sheets, fix cards and the row table
      // for all files). `reviewUrl` is `/import/<batchId>`.
      const data = await res.json() as { batchId: string };
      const classLabel = ASSET_IMPORT_ASSET_CLASSES.find((c) => c.id === assetClass)?.label ?? assetClass;
      addStoredBatch({ batchId: data.batchId, label: `${classLabel} - ${file.name}` });
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      await navigate({ to: '/import/$batchId', params: { batchId: data.batchId } });
    } catch (ex: unknown) {
      setUploadErr((ex as Error).message);
    } finally {
      setUploading(false);
    }
  }

  function removeBatch(batchId: string) {
    removeStoredBatch(batchId);
    setBatches((prev) => prev.filter((b) => b.batchId !== batchId));
  }

  return (
    <div className="space-y-6">
      <PanelTitle
        title="Upload your inventory"
        description="Upload one or more CSV files. Each file should contain assets of the same class."
      />

      {/* Template download + column reference (SIL-15) */}
      <div className="space-y-3 rounded-lg border border-border bg-card p-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-medium text-foreground">Need a starting point?</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Download the template, fill in one row per asset, then upload it below.
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              These are the <span className="font-medium text-foreground">SiliconX platform columns</span>.
              Rename the columns in your ERP export (or fixed register) to match the names below so
              each source column lands in the right SiliconX field.
            </p>
          </div>
          <button
            type="button"
            onClick={downloadAssetTemplate}
            className="inline-flex flex-none items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
          >
            Download CSV template
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                <th className="py-1.5 pr-4 font-medium">SiliconX column</th>
                <th className="py-1.5 pr-4 font-medium">Required</th>
                <th className="py-1.5 font-medium">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {ASSET_IMPORT_COLUMNS.map((col) => (
                <tr key={col.key}>
                  <td className="py-1.5 pr-4 font-mono text-foreground">{col.key}</td>
                  <td className="py-1.5 pr-4">
                    <span
                      className={cn(
                        'rounded px-1.5 py-0.5 text-xs font-medium',
                        col.requirement === 'required'
                          ? 'bg-red-50 text-red-700'
                          : col.requirement === 'recommended'
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-muted text-muted-foreground',
                      )}
                    >
                      {col.requirement}
                    </span>
                  </td>
                  <td className="py-1.5 text-muted-foreground">
                    {col.hint}
                    {col.allowed && (
                      <span className="text-muted-foreground"> One of: {col.allowed.join(', ')}.</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upload form */}
      <form onSubmit={handleUpload} className="rounded-lg border border-border bg-muted p-4 space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Asset Class" required>
            <select
              value={assetClass}
              onChange={(e) => setAssetClass(e.target.value)}
              className={inputCls()}
            >
              {ASSET_IMPORT_ASSET_CLASSES.map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
          </Field>
          <Field label="CSV File" required>
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              onChange={(e) => { setFile(e.target.files?.[0] ?? null); setUploadErr(null); setRejected(null); }}
              className="w-full rounded-md border border-border px-3 py-1.5 text-sm file:mr-3 file:rounded file:border-0 file:bg-brand-50 file:px-3 file:py-1 file:text-xs file:font-medium file:text-brand-700 hover:file:bg-brand-100"
            />
          </Field>
        </div>
        {uploadErr && <p className="text-xs text-red-600">{uploadErr}</p>}
        {rejected && (
          <div role="alert" className="space-y-3 rounded-md border border-red-300 bg-red-50 p-3">
            <p className="text-sm font-medium text-red-800">
              Upload failed: {rejected.filename}
            </p>
            <p className="text-xs text-red-700">{rejected.message}</p>
            <ImportRowIssues errors={rejected.rowErrors} warnings={rejected.rowWarnings} />
          </div>
        )}
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={uploading || !file}
          >
            {uploading ? 'Uploading…' : 'Upload Batch'}
          </Button>
        </div>
      </form>

      {/* Per-row findings for batches that were partly accepted */}
      {batches.some((b) => b.rowErrors.length > 0 || (b.rowWarnings?.length ?? 0) > 0) && (
        <div className="space-y-4">
          {batches
            .filter((b) => b.rowErrors.length > 0 || (b.rowWarnings?.length ?? 0) > 0)
            .map((b) => (
              <div key={b.batchId} className="space-y-2">
                <p className="text-sm font-medium text-foreground">{b.label ?? b.batchId}</p>
                <ImportRowIssues errors={b.rowErrors} warnings={b.rowWarnings} />
              </div>
            ))}
        </div>
      )}

      {/* Uploaded batches */}
      {batches.length > 0 && (
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted text-left text-xs font-medium text-muted-foreground">
                <th className="px-4 py-2">Batch</th>
                <th className="px-4 py-2">Accepted</th>
                <th className="px-4 py-2">Total rows</th>
                <th className="px-4 py-2">Skipped</th>
                <th className="px-4 py-2">Errors</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {batches.map((b) => (
                <tr key={b.batchId} className="hover:bg-muted">
                  <td className="px-4 py-2 font-medium text-foreground">{b.label ?? b.batchId}</td>
                  <td className="px-4 py-2 text-muted-foreground">{b.rowCountAccepted}</td>
                  <td className="px-4 py-2 text-muted-foreground">{b.rowCountTotal}</td>
                  <td className="px-4 py-2">
                    {b.rowCountSkipped ? (
                      <span className="text-muted-foreground">{b.rowCountSkipped} example</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {b.rowErrors.length > 0 ? (
                      <span className="text-red-600">
                        {b.rowErrors.length} rejected row{b.rowErrors.length === 1 ? '' : 's'}
                      </span>
                    ) : (
                      <span className="text-green-600">None</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => removeBatch(b.batchId)}
                      className="text-xs text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex justify-between pt-2">
        <button
          type="button"
          onClick={onBack}
          className="rounded-md px-4 py-2 text-sm text-muted-foreground hover:bg-muted"
        >
          Back
        </button>
        <Button className="px-5"
          onClick={() => onNext({ batches })}
        >
          {batches.length === 0 ? 'Skip' : 'Next'}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add Prometheus Target modal
// ---------------------------------------------------------------------------

interface AddTargetModalProps {
  open: boolean;
  onClose: () => void;
  onAdded: (target: PrometheusTarget) => void;
}

function AddTargetModal({ open, onClose, onAdded }: AddTargetModalProps) {
  const [label, setLabel] = useState('');
  const [scrapeUrl, setScrapeUrl] = useState('');
  const [scrapeInterval, setScrapeInterval] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // After creation
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [createdTarget, setCreatedTarget] = useState<PrometheusTarget | null>(null);
  const [tokenSaved, setTokenSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  function reset() {
    setLabel('');
    setScrapeUrl('');
    setScrapeInterval('');
    setErr(null);
    setCreatedToken(null);
    setCreatedTarget(null);
    setTokenSaved(false);
    setCopied(false);
    setSubmitting(false);
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (!label.trim()) { setErr('Label is required.'); return; }
    if (!scrapeUrl.trim()) { setErr('Scrape URL is required.'); return; }
    setErr(null);
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        label: label.trim(),
        scrapeUrl: scrapeUrl.trim(),
      };
      if (scrapeInterval.trim()) {
        const n = parseInt(scrapeInterval.trim(), 10);
        if (isNaN(n) || n <= 0) { setErr('Scrape interval must be a positive number.'); setSubmitting(false); return; }
        body.scrapeIntervalSeconds = n;
      }
      const res = await api.api.client['prometheus-targets'].$post({
        json: body as { label: string; scrapeUrl: string; scrapeIntervalSeconds?: number },
      });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      setCreatedTarget({ targetId: data.targetId, label: data.label });
      setCreatedToken(data.token);
    } catch (ex: unknown) {
      setErr((ex as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function copyToken() {
    if (!createdToken) return;
    await navigator.clipboard.writeText(createdToken);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleDone() {
    if (createdTarget) onAdded(createdTarget);
    handleClose();
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md rounded-xl bg-card p-6 shadow-xl">
        {!createdToken ? (
          <>
            <h2 className="mb-4 text-base font-semibold text-foreground">Add Prometheus Target</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Field label="Label" required>
                <input
                  value={label}
                  onChange={(e) => { setLabel(e.target.value); setErr(null); }}
                  placeholder="e.g. GPU Rack A"
                  className={inputCls()}
                />
              </Field>
              <Field label="Scrape URL" required hint="Full URL to your Prometheus metrics endpoint">
                <input
                  value={scrapeUrl}
                  onChange={(e) => { setScrapeUrl(e.target.value); setErr(null); }}
                  placeholder="https://host:9091/metrics"
                  className={inputCls()}
                />
              </Field>
              <Field label="Scrape Interval (seconds)" hint="Optional — defaults to 60">
                <input
                  type="number"
                  min={1}
                  value={scrapeInterval}
                  onChange={(e) => { setScrapeInterval(e.target.value); setErr(null); }}
                  placeholder="60 (default)"
                  className={inputCls()}
                />
              </Field>
              {err && <p className="text-xs text-red-600">{err}</p>}
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleClose}
                  className="rounded-md px-4 py-2 text-sm text-muted-foreground hover:bg-muted"
                >
                  Cancel
                </button>
                <Button
                  type="submit"
                  disabled={submitting}
                >
                  {submitting ? 'Creating…' : 'Create Target'}
                </Button>
              </div>
            </form>
          </>
        ) : (
          <>
            <h2 className="mb-2 text-base font-semibold text-foreground">Bearer Token (shown once)</h2>
            <p className="mb-4 text-sm text-muted-foreground">
              Copy and save this token securely. It will not be shown again.
            </p>
            <div className="mb-4 flex items-start gap-2 rounded-lg border border-border bg-muted p-3">
              <code className="flex-1 break-all font-mono text-xs text-foreground">{createdToken}</code>
              <button
                type="button"
                onClick={copyToken}
                className="shrink-0 rounded-md border border-border bg-card px-3 py-1 text-xs font-medium text-foreground hover:bg-muted"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <label className="mb-4 flex cursor-pointer items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={tokenSaved}
                onChange={(e) => setTokenSaved(e.target.checked)}
                className="size-4 rounded border-border accent-brand-600"
              />
              I've saved this token securely
            </label>
            <div className="flex justify-end">
              <Button className="px-5"
                disabled={!tokenSaved}
                onClick={handleDone}
              >
                Done
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — Configure Telemetry
// ---------------------------------------------------------------------------

function Step2({
  state,
  onNext,
  onBack,
}: {
  state: WizardState;
  onNext: (updates: Pick<WizardState, 'batchAssignments' | 'targets'>) => void;
  onBack: () => void;
}) {
  const [targets, setTargets] = useState<PrometheusTarget[]>(state.targets);
  const [assignments, setAssignments] = useState<BatchAssignment[]>(() => {
    // Pre-populate from existing state or default to csv
    return state.batches.map((b) => {
      const existing = state.batchAssignments.find((a) => a.batchId === b.batchId);
      return existing ?? { batchId: b.batchId, method: 'csv' };
    });
  });
  const [addModalOpen, setAddModalOpen] = useState(false);

  function handleAssignmentChange(
    batchId: string,
    value: string,
  ) {
    setAssignments((prev) =>
      prev.map((a) => {
        if (a.batchId !== batchId) return a;
        if (value === 'csv') return { batchId, method: 'csv' };
        return { batchId, method: 'prometheus', prometheusTargetId: value };
      }),
    );
  }

  function handleTargetAdded(target: PrometheusTarget) {
    setTargets((prev) => [...prev, target]);
  }

  function handleNext() {
    onNext({ batchAssignments: assignments, targets });
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Configure Telemetry</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Choose how telemetry data will be collected for each uploaded batch. You can change this
          later from your inventory page.
        </p>
      </div>

      {/* Info callout */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
        <strong>Note:</strong> Telemetry settings are saved now and will apply once your inventory
        is approved by the SiliconX team.
      </div>

      {/* Per-batch assignment */}
      {state.batches.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">No batches uploaded yet.</p>
      ) : (
        <div className="space-y-3">
          {state.batches.map((batch) => {
            const assignment = assignments.find((a) => a.batchId === batch.batchId);
            const selectValue =
              assignment?.method === 'prometheus' && assignment.prometheusTargetId
                ? assignment.prometheusTargetId
                : 'csv';

            return (
              <div
                key={batch.batchId}
                className="flex flex-col gap-2 rounded-lg border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <p className="text-sm font-medium text-foreground">{batch.label ?? batch.batchId}</p>
                  <p className="text-xs text-muted-foreground">
                    {batch.rowCountAccepted} asset{batch.rowCountAccepted !== 1 ? 's' : ''} accepted
                  </p>
                </div>
                <select
                  value={selectValue}
                  onChange={(e) => handleAssignmentChange(batch.batchId, e.target.value)}
                  className="w-full rounded-md border border-border px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 sm:w-64"
                >
                  <option value="csv">CSV (manual upload)</option>
                  {targets.map((t) => (
                    <option key={t.targetId} value={t.targetId}>
                      Prometheus: {t.label}
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Prometheus target */}
      <div>
        <button
          type="button"
          onClick={() => setAddModalOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground hover:bg-muted"
        >
          + Add Prometheus Target
        </button>
        {targets.length > 0 && (
          <p className="mt-2 text-xs text-muted-foreground">
            {targets.length} Prometheus target{targets.length !== 1 ? 's' : ''} configured
          </p>
        )}
      </div>

      <div className="flex justify-between pt-2">
        <button
          type="button"
          onClick={onBack}
          className="rounded-md px-4 py-2 text-sm text-muted-foreground hover:bg-muted"
        >
          Back
        </button>
        <Button className="px-5"
          onClick={handleNext}
        >
          Next
        </Button>
      </div>

      <AddTargetModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onAdded={handleTargetAdded}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — Done
// ---------------------------------------------------------------------------

function Step3({
  state,
  onBack,
  alreadyOnboarded = false,
}: {
  state: WizardState;
  onBack: () => void;
  /** RUF-373 — org already had onboardingCompleted: true when this wizard
   * pass started (repeat "Import assets" run). Skip the redundant
   * onboarding/complete PATCH and land back on Assets instead of Dashboard. */
  alreadyOnboarded?: boolean;
}) {
  const navigate = useNavigate();
  const [completing, setCompleting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // SIL-642: country is required to complete onboarding (customer_org_
  // jurisdiction, db/schema/758). Only collected on the first-time pass —
  // an already-onboarded org already has one (set at signup or a prior
  // completion) and this screen doesn't re-collect it on a repeat import.
  const [countryCode, setCountryCode] = useState('');

  const totalAccepted = state.batches.reduce((sum, b) => sum + b.rowCountAccepted, 0);
  const prometheusCount = state.targets.length;

  const csvOnlyBatchCount = state.batchAssignments.filter((a) => a.method === 'csv').length;

  async function handleGoToDashboard() {
    setErr(null);
    if (!alreadyOnboarded && !countryCode) {
      setErr('Please select your organization’s country before continuing.');
      return;
    }
    setCompleting(true);
    try {
      // Repeat import for an already-onboarded org: don't re-PATCH
      // onboarding/complete — it's a no-op that only touches the
      // already-true flag, and the wizard shouldn't ever write company-info
      // data on a bulk-import pass.
      if (!alreadyOnboarded) {
        const res = await api.api.client.onboarding.complete.$patch({ json: { countryCode } });
        if (!res.ok) {
          await throwIfNotOk(res);
          throw new Error(`HTTP ${res.status}`);
        }
        // Onboarding flag changed in the DB — drop the memoized identity so
        // the root beforeLoad re-fetches and doesn't bounce us back to /onboarding.
        clearClientIdentity();
      }
      clearStoredBatches();
      navigate({ to: alreadyOnboarded ? '/assets' : '/dashboard' });
    } catch (ex: unknown) {
      setErr((ex as Error).message);
      setCompleting(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-full bg-green-100">
          <svg
            className="size-7 text-green-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-foreground">
          {totalAccepted > 0 ? "You're all set!" : 'Almost there'}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">Here's a summary of your onboarding.</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-border bg-card p-4 text-center">
          <p className="text-2xl font-semibold tabular-nums text-foreground">{totalAccepted}</p>
          <p className="mt-1 text-xs text-muted-foreground">Asset{totalAccepted !== 1 ? 's' : ''} submitted for review</p>
        </div>
        <div className="rounded-lg border border-border bg-card p-4 text-center">
          <p className="text-2xl font-semibold tabular-nums text-foreground">{prometheusCount}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Prometheus target{prometheusCount !== 1 ? 's' : ''} configured
          </p>
        </div>
        <div className="rounded-lg border border-border bg-card p-4 text-center">
          <p className="text-2xl font-semibold tabular-nums text-foreground">{csvOnlyBatchCount}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Batch{csvOnlyBatchCount !== 1 ? 'es' : ''} using CSV telemetry
          </p>
        </div>
      </div>

      {/* SIL-642: country is required before onboarding can complete */}
      {!alreadyOnboarded && (
        <Field
          label="Organization country"
          required
          hint="Used for export-control and jurisdiction review — this cannot be changed later without contacting SiliconX support."
        >
          <select
            value={countryCode}
            onChange={(e) => { setCountryCode(e.target.value); setErr(null); }}
            className={inputCls()}
          >
            <option value="">Select a country…</option>
            {COUNTRY_OPTIONS.map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
        </Field>
      )}

      {/* Pending review callout, or an explicit 0-assets warning (SIL-1589) */}
      {totalAccepted > 0 ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Your inventory is pending review by the SiliconX team. Assets will appear in your portal
          once approved.
        </div>
      ) : (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          No assets have been imported, so nothing is pending review. You can finish setup now and
          import your inventory later from Assets, or go back and upload a corrected file.
        </div>
      )}

      {/* Telemetry note */}
      <p className="text-sm text-muted-foreground">
        Telemetry settings saved. They'll apply once your inventory is approved.
      </p>

      {err && <p className="text-sm text-red-600">{err}</p>}

      <div className="flex justify-between pt-2">
        <button
          type="button"
          onClick={onBack}
          disabled={completing}
          className="rounded-md px-4 py-2 text-sm text-muted-foreground hover:bg-muted disabled:opacity-50"
        >
          Back
        </button>
        <Button className="px-6"
          onClick={handleGoToDashboard}
          disabled={completing || (!alreadyOnboarded && !countryCode)}
        >
          {completing ? 'Completing…' : 'Go to Dashboard'}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main wizard orchestrator
// ---------------------------------------------------------------------------

function OnboardingPage() {
  const navigate = useNavigate();

  // Framing + completion semantics depend on the org's onboarding state, so
  // hold off initializing wizard state until identity resolves. Memoized/shared
  // with the dashboard's identity fetch (react-query dedupes by key).
  const identityQ = useQuery({
    queryKey: qk.identity(),
    queryFn: () => fetchClientIdentity(),
  });

  const [state, setState] = useState<WizardState | null>(null);

  useEffect(() => {
    if (state !== null || !identityQ.data) return;
    // SIL-1617: uploads continue on /import/<id> and come back here; rebuild the
    // batch list from the stored ids + the DB (counts are never trusted from storage).
    void (async () => {
      const batches: BatchMeta[] = [];
      for (const s of loadStoredBatches()) {
        try {
          const res = await api.api.client.import.batches[':batchId'].review.$get({ param: { batchId: s.batchId } });
          if (!res.ok) { removeStoredBatch(s.batchId); continue; }
          const r = (await res.json()) as unknown as ReviewPayload;
          batches.push({
            batchId: s.batchId,
            label: s.label,
            rowCountAccepted: r.counts.accepted,
            rowCountTotal: r.counts.total,
            rowErrors: [],
          });
        } catch {
          /* transient: leave it in storage, skip for now */
        }
      }
      setState({ step: 1, batches, targets: [], batchAssignments: [] });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityQ.data]);

  if (!state) {
    return (
      <div className="mx-auto max-w-2xl py-12 text-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }

  // Repeat-import pass: an org that already finished onboarding. Skip the
  // completion PATCH, use "Import assets" framing, and land back on /assets.
  // Company identity is created with the customer org at signup, so the wizard
  // never collects it — the first step is always Upload CSV.
  const alreadyOnboarded = identityQ.data?.onboardingCompleted ?? false;

  function goTo(step: WizardState['step']) {
    setState((s) => (s ? { ...s, step } : s));
  }

  function handleStep1Next(updates: Pick<WizardState, 'batches'>) {
    setState((s) => (s ? { ...s, ...updates, step: 2 } : s));
  }

  function handleStep2Next(updates: Pick<WizardState, 'batchAssignments' | 'targets'>) {
    setState((s) => (s ? { ...s, ...updates, step: 3 } : s));
  }

  return (
    <div className="mx-auto max-w-2xl">
      {/* Header */}
      <div className="mb-8">
        <PageHeader
          title={alreadyOnboarded ? 'Import assets' : 'Welcome to SiliconX'}
          subtitle={
            alreadyOnboarded
              ? 'Upload another batch of inventory to add to your existing assets.'
              : 'Complete these steps to set up your seller account.'
          }
        />
      </div>

      {/* Step indicator */}
      <div className="mb-8 flex justify-center">
        <StepIndicator current={state.step} />
      </div>

      {/* Card */}
      <Card>
        {state.step === 1 && (
          <Step1
            state={state}
            onNext={handleStep1Next}
            onBack={() => navigate({ to: alreadyOnboarded ? '/assets' : '/dashboard' })}
          />
        )}
        {state.step === 2 && (
          <Step2 state={state} onNext={handleStep2Next} onBack={() => goTo(1)} />
        )}
        {state.step === 3 && (
          <Step3 state={state} onBack={() => goTo(2)} alreadyOnboarded={alreadyOnboarded} />
        )}
      </Card>
    </div>
  );
}
